import posthog, { type PostHogInterface } from 'posthog-js';

import { UserModel } from '@/models/UserModel';

import { Loadable } from './Loadable';

export class PostHog extends Loadable {
  public static identify(user: UserModel): void {
    if (!PostHog.isEnabled()) {
      return;
    }

    try {
      posthog.identify(user.id, {
        email: user.email,
        name: user.fullName,
        first_name: user.first_name,
        last_name: user.last_name,
        username: user.username ?? undefined
      });

      if (user.organization_details) {
        posthog.group('organization', user.organization_details.id, {
          name: user.organization_details.name
        });
      }
    } catch (error) {
      console.error('PostHog identify failed:', error);
    }
  }

  public static reset(): void {
    if (!PostHog.isEnabled()) {
      return;
    }

    try {
      posthog.reset();
    } catch (error) {
      console.error('PostHog reset failed:', error);
    }
  }

  public load(): void {
    if (!PostHog.isEnabled()) {
      return;
    }

    try {
      posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
        api_host: 'https://us.i.posthog.com',
        autocapture: {
          dom_event_allowlist: ['click'],
          element_allowlist: ['a', 'button'],
          css_selector_allowlist: [
            '[role="button"]',
            '[role="menuitem"]',
            '[role="option"]',
            '.MuiSwitch-root',
            '.MuiCheckbox-root'
          ]
        },
        capture_pageview: true,
        capture_pageleave: true,
        mask_all_text: false,
        mask_all_element_attributes: false,
        disable_session_recording: true,
        persistence: 'localStorage+cookie',
        capture_performance: true,
        loaded: (ph: PostHogInterface) => {
          if (import.meta.env.DEV) {
            console.log('PostHog initialized successfully');
            ph.debug();
          }
        },
        on_xhr_error: (failedRequest: XMLHttpRequest) => {
          if (import.meta.env.DEV) {
            console.error('PostHog XHR error:', failedRequest);
          }
        }
      });
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }
  }

  private static isEnabled(): boolean {
    if (!import.meta.env.VITE_POSTHOG_API_KEY) {
      console.warn('PostHog API key not configured. Skipping initialization.');
      return false;
    }

    if (import.meta.env.MODE === 'test') {
      console.log('PostHog disabled in test environment');
      return false;
    }

    return true;
  }
}
